import { useState, useEffect, useRef, useCallback } from 'react';
import Editable from '../Editable';
import { authFetch } from '../../auth';
import { ContactFormPreview } from '../FormPreview';

const API = '/api/cms/sections';

/** 회사 소개 — about 페이지 섹션(TB_PAGE_SECTION)을 불러와 편집·저장. */
export default function About({ onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [secs, setSecs] = useState(null);
  const ref = useRef([]);
  const dirtyRef = useRef(false);

  useEffect(() => {
    let alive = true;
    authFetch(`${API}?page=about&lang=ko`).then((r) => r.json())
      .then((d) => { if (alive) { ref.current = d; setSecs(d); } })
      .catch(() => { if (alive) setSecs([]); });
    return () => { alive = false; };
  }, [refreshKey]);

  const get = (key) => (ref.current.find((s) => s.key === key) || {}).data || {};
  const updSec = (key, mut) => { dirtyRef.current = true; ref.current = ref.current.map((s) => (s.key === key ? { ...s, data: mut(s.data || {}) } : s)); setSecs(ref.current); };
  const f = (key, field, v) => updSec(key, (d) => ({ ...d, [field]: v }));
  const item = (key, i, field, v) => updSec(key, (d) => ({ ...d, items: d.items.map((x, idx) => (idx === i ? { ...x, [field]: v } : x)) }));
  const hl = (i, field, v) => updSec('ceo', (d) => ({ ...d, highlights: d.highlights.map((x, idx) => (idx === i ? { ...x, [field]: v } : x)) }));

  const save = useCallback(async () => {
    for (const s of ref.current) {
      const res = await authFetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: 'about', key: s.key, type: s.type, collection: s.collection, sort: s.sort, dataKo: s.data }),
      });
      if (!res.ok) throw new Error(`회사소개 저장 실패 (${res.status})`);
    }
    dirtyRef.current = false;
  }, []);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current, []);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  if (secs === null) {
    return <section className="section pt-page"><div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div></section>;
  }

  const hero = get('hero'), stats = get('stats'), ceo = get('ceo'), phil = get('philosophy'), cul = get('culture'), cta = get('cta');

  return (
    <>
      <section className="section pt-page">
        <div className="container">
          <div className="about-grid">
            <div className="about-txt">
              <Editable as="h1" className="heading-page" value={hero.title || ''} onChange={(t) => f('hero', 'title', t)} />
              <Editable as="p" className="about-intro-edit" value={hero.intro || ''} onChange={(t) => f('hero', 'intro', t)} />
            </div>
            <div className="overflow-hidden"><img src="/images/about/about-team.png" alt="AceTec 팀 소개" className="img-tall" loading="lazy" /></div>
          </div>
        </div>
      </section>

      <section className="section about-stats-section">
        <div className="container">
          <div className="about-stats">
            {(stats.items || []).map((s, i) => (
              <div className="stat-card" key={i}>
                <Editable as="span" className="stat-number" value={s.number} onChange={(t) => item('stats', i, 'number', t)} />
                <Editable as="span" className="stat-label" value={s.label} onChange={(t) => item('stats', i, 'label', t)} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section about-ceo-section">
        <div className="container">
          <Editable as="h2" className="about-section-title" value={ceo.title || ''} onChange={(t) => f('ceo', 'title', t)} />
          <div className="ceo-grid">
            <div className="ceo-message">
              <Editable as="p" className="ceo-quote" value={ceo.quote || ''} onChange={(t) => f('ceo', 'quote', t)} />
              <Editable as="p" value={ceo.body1 || ''} onChange={(t) => f('ceo', 'body1', t)} />
              <Editable as="p" value={ceo.body2 || ''} onChange={(t) => f('ceo', 'body2', t)} />
              <Editable as="p" value={ceo.body3 || ''} onChange={(t) => f('ceo', 'body3', t)} />
              <Editable as="p" value={ceo.body4 || ''} onChange={(t) => f('ceo', 'body4', t)} />
              <Editable as="p" className="ceo-name" value={ceo.sign || ''} onChange={(t) => f('ceo', 'sign', t)} />
            </div>
            <div className="ceo-highlights">
              {(ceo.highlights || []).map((h, i) => (
                <div className="highlight-card" key={i}>
                  <span className="highlight-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" /><path d="m9 12 2 2 4-4" /></svg>
                  </span>
                  <Editable as="h4" value={h.title} onChange={(t) => hl(i, 'title', t)} />
                  <Editable as="p" value={h.desc} onChange={(t) => hl(i, 'desc', t)} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section about-philosophy-section">
        <div className="container">
          <Editable as="h2" className="about-section-title" value={phil.title || ''} onChange={(t) => f('philosophy', 'title', t)} />
          <Editable as="p" className="about-section-sub" value={phil.subtitle || ''} onChange={(t) => f('philosophy', 'subtitle', t)} />
          <div className="philosophy-grid">
            {(phil.items || []).map((p, i) => (
              <div className="philosophy-card" key={i}>
                <Editable as="div" className="philosophy-number" value={p.no} onChange={(t) => item('philosophy', i, 'no', t)} />
                <Editable as="h3" value={p.title} onChange={(t) => item('philosophy', i, 'title', t)} />
                <Editable as="p" value={p.desc} onChange={(t) => item('philosophy', i, 'desc', t)} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section about-culture-section">
        <div className="container">
          <Editable as="h2" className="about-section-title" value={cul.title || ''} onChange={(t) => f('culture', 'title', t)} />
          <Editable as="p" className="about-section-sub" value={cul.subtitle || ''} onChange={(t) => f('culture', 'subtitle', t)} />
          <div className="culture-grid">
            {(cul.items || []).map((cc, i) => (
              <div className="culture-card" key={i}>
                <div className="culture-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" /><path d="M9 18h6" /><path d="M10 22h4" /></svg>
                </div>
                <Editable as="h3" value={cc.title} onChange={(t) => item('culture', i, 'title', t)} />
                <Editable as="p" value={cc.desc} onChange={(t) => item('culture', i, 'desc', t)} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="contact-bright section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-txt">
              <Editable as="h2" value={cta.title || ''} onChange={(t) => f('cta', 'title', t)} />
              <Editable as="p" value={cta.desc || ''} onChange={(t) => f('cta', 'desc', t)} />
            </div>
            <ContactFormPreview />
          </div>
        </div>
      </section>
    </>
  );
}
